/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { localize } from '../../translations.js';
import { SupportedLocale } from '../../model.js';
import { withEnterKey } from '../../internal/with-enter-key.js';
import * as Assets from '../../assets.js';
import * as Styles from '../../styles.js';
/**
 * States.
 */
export const ModalAction = {
    SUBMIT: 'SUBMIT',
    CANCEL: 'CANCEL',
    CLOSE: 'CLOSE',
};
/**
 * WebComponent.
 */
let PwcModal = class PwcModal extends LitElement {
    constructor() {
        super(...arguments);
        this.locale = SupportedLocale.en_US;
        this.hasSubmitButton = false;
        this.hasCancelButton = false;
        this.isSubmitDisabled = false;
    }
    /**
     * Render the header.
     */
    _renderHeader() {
        let headerExtraClassName;
        if (this.modalTitle) {
            headerExtraClassName = 'has-title';
        }
        else {
            headerExtraClassName = 'has-no-title';
        }
        return html `
      <div class="header ${headerExtraClassName}">
        <div class="title">${this.modalTitle}</div>
        <div
          class="close"
          @click=${() => {
            this._handleModalAction(ModalAction.CLOSE);
        }}
          @keypress=${(event) => {
            withEnterKey(event, () => this._handleModalAction(ModalAction.CLOSE));
        }}
        >
          ${Assets.iconClose()}
        </div>
      </div>
    </div>
  `;
    }
    /**
     * Render the footer.
     */
    _renderFooter() {
        // no button, no footer.
        if (!this.hasSubmitButton && !this.hasCancelButton) {
            return html ``;
        }
        // render submit button.
        let submitButtonTemplate;
        if (this.hasSubmitButton) {
            submitButtonTemplate = html `<button
        class="blue"
        .disabled=${this.isSubmitDisabled}
        @click=${() => {
                this._handleModalAction(ModalAction.SUBMIT);
            }}
        @keypress=${(event) => {
                withEnterKey(event, () => this._handleModalAction(ModalAction.SUBMIT));
            }}
      >
        ${localize(this.locale, 'ai.chat.submit')}
      </button>`;
        }
        // render cancel button.
        let cancelButtonTemplate;
        if (this.hasCancelButton) {
            cancelButtonTemplate = html `<button
        @click=${() => {
                this._handleModalAction(ModalAction.CANCEL);
            }}
        @keypress=${(event) => {
                withEnterKey(event, () => this._handleModalAction(ModalAction.CANCEL));
            }}
      >
        ${localize(this.locale, 'ai.chat.cancel')}
      </button>`;
        }
        return html `
      <div class="footer">${cancelButtonTemplate}${submitButtonTemplate}</div>
    `;
    }
    /**
     * @inheritdoc
     */
    render() {
        return html `
      <div class="modal-dialog">
        ${this._renderHeader()}
        <slot></slot>
        ${this._renderFooter()}
      </div>
    `;
    }
    /**
     * Handle the close mouse click event.
     */
    _handleModalAction(action) {
        // do not emit submit if disabled.
        if (action === ModalAction.SUBMIT && this.isSubmitDisabled) {
            return;
        }
        // create custom event.
        const event = new CustomEvent('pwc-modal-action-event', {
            detail: {
                action,
            },
        });
        // dispatch it.
        this.dispatchEvent(event);
    }
};
// styles.
PwcModal.styles = [
    css `
      :host {
        /* variables */
        --pwc-modal-background-color: var(
          --pwc-overlay-background-color,
          rgb(162 177 185 / 70%)
        );

        --pwc-modal-background-inset: var(--pwc-inset-modal, 0 0 0 0);

        display: flex;
        justify-content: center;
        align-items: center;
        position: fixed;
        inset: var(--pwc-modal-background-inset);
        background: var(--pwc-modal-background-color);

        /* font style */
        --pwc-modal-font-size-large: var(--pwc-font-size-large, 1.25rem);
        --pwc-modal-title-font-weight: 700;
      }

      .modal-dialog {
        display: flex;
        flex: none;
        flex-direction: column;
        max-width: 100%;
        background-color: white;
        border-radius: 12px;
        border: 1px solid #e0e0e0;
        box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.2);
      }

      /* Header */
      .modal-dialog .header {
        display: flex;
        flex-direction: row;
        flex: auto;
        align-items: center;
      }

      .modal-dialog .header .title {
        flex: auto;
        color: #212121;
        font-size: var(--pwc-modal-font-size-large);
        font-weight: var(--pwc-modal-title-font-weight);
        padding: 0px 15px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .modal-dialog .header .close {
        flex: none;
        width: 55px;
        height: 55px;
        cursor: pointer;
      }

      .modal-dialog .header .close .icon-close {
        padding: 21px;
        width: 12px;
        height: 12px;
      }

      .modal-dialog .header.has-title {
        border-bottom: 1px solid #e0e0e0;
      }

      .modal-dialog .footer {
        display: flex;
        flex-direction: row-reverse;
        border-top: 1px solid #e0e0e0;
        padding: 10px 15px;
      }

      .modal-dialog .footer button {
        margin: 5px;
      }
    `,
    Styles.button,
];
__decorate([
    property({ attribute: 'locale', type: String })
], PwcModal.prototype, "locale", void 0);
__decorate([
    property({ attribute: 'modal-title' })
], PwcModal.prototype, "modalTitle", void 0);
__decorate([
    property({ attribute: 'has-submit-button', type: Boolean })
], PwcModal.prototype, "hasSubmitButton", void 0);
__decorate([
    property({ attribute: 'has-cancel-button', type: Boolean })
], PwcModal.prototype, "hasCancelButton", void 0);
__decorate([
    property({ attribute: 'is-submit-disabled', type: Boolean })
], PwcModal.prototype, "isSubmitDisabled", void 0);
PwcModal = __decorate([
    customElement('pwc-modal')
], PwcModal);
export { PwcModal };
//# sourceMappingURL=pwc-modal.js.map